import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Copy, Check, Languages, ChevronDown, RotateCcw, History, Upload, Download, FileSpreadsheet, AlertTriangle, CheckCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import mammoth from 'mammoth';
import { useData } from '../contexts/DataContext';
import { translateText } from '../lib/prompt';
import { VocabCollectionSelector } from '../components/VocabCollectionSelector';

const MARKETS = [
  { code: 'SE', label: 'Sverige',       flag: '🇸🇪', lang: 'svenska' },
  { code: 'NO', label: 'Norge',         flag: '🇳🇴', lang: 'norska' },
  { code: 'FI', label: 'Finland',       flag: '🇫🇮', lang: 'finska' },
  { code: 'DK', label: 'Danmark',       flag: '🇩🇰', lang: 'danska' },
  { code: 'DE', label: 'Tyskland',      flag: '🇩🇪', lang: 'tyska' },
  { code: 'NL', label: 'Nederländerna', flag: '🇳🇱', lang: 'nederländska (Nederländerna)' },
];

const SOURCE_LANGS = [
  { code: 'SE', label: 'Svenska',      flag: '🇸🇪' },
  { code: 'NO', label: 'Norska',       flag: '🇳🇴' },
  { code: 'FI', label: 'Finska',       flag: '🇫🇮' },
  { code: 'DK', label: 'Danska',       flag: '🇩🇰' },
  { code: 'DE', label: 'Tyska',        flag: '🇩🇪' },
  { code: 'NL', label: 'Nederländska', flag: '🇳🇱' },
  { code: 'EN', label: 'Engelska',     flag: '🇬🇧' },
];

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }, [text]);
  return (
    <button className="translate-copy-btn" onClick={handleCopy} title="Kopiera text" data-testid="copy-translation-btn">
      {copied ? <Check size={13} /> : <Copy size={13} />}
      {copied ? 'Kopierad!' : 'Kopiera'}
    </button>
  );
}

function TranslationCard({ market, result, isLoading, onRetranslate }) {
  return (
    <div className={`translate-card${isLoading ? ' translate-card--loading' : ''}${result?.text ? ' translate-card--done' : ''}`}
         data-testid={`translation-card-${market.code}`}>
      <div className="translate-card-header">
        <span className="translate-card-market">
          <span className="translate-flag">{market.flag}</span>
          {market.label}
        </span>
        <div className="translate-card-meta">
          {result?.text && !isLoading && (
            <>
              <span className="translate-char-count" data-testid={`char-count-${market.code}`}>
                {result.text.length} tecken
              </span>
              <button
                className="translate-retranslate-btn"
                onClick={() => onRetranslate(market.code)}
                title="Översätt om (använder aktuella instruktioner)"
                data-testid={`retranslate-btn-${market.code}`}
              >
                <RotateCcw size={12} />
                Översätt om
              </button>
              <CopyButton text={result.text} />
            </>
          )}
          {result?.error && !isLoading && (
            <button
              className="translate-retranslate-btn"
              onClick={() => onRetranslate(market.code)}
              data-testid={`retranslate-btn-${market.code}`}
            >
              <RotateCcw size={12} />
              Försök igen
            </button>
          )}
        </div>
      </div>
      <div className="translate-card-body">
        {isLoading && (
          <div className="translate-spinner-wrap">
            <div className="translate-spinner" />
            <span>Översätter till {market.lang}…</span>
          </div>
        )}
        {!isLoading && result?.error && (
          <p className="translate-error">{result.error}</p>
        )}
        {!isLoading && result?.text && (
          <p className="translate-output" data-testid={`translation-output-${market.code}`}>
            {result.text}
          </p>
        )}
        {!isLoading && !result && (
          <p className="translate-placeholder">Översättning visas här…</p>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TRANSLATE FILE — general-purpose multi-format translation tool
// ─────────────────────────────────────────────────────────────────────────────

/** Auto-detect which columns contain translatable text */
function detectColumns(headers, rows) {
  return headers.map(header => {
    const values = rows.map(r => r[header]).filter(v => v != null && String(v).trim() !== '');
    if (!values.length) return { name: header, auto: false, selected: false, sample: [] };
    const numericCount = values.filter(v => typeof v === 'number' && !isNaN(v)).length;
    if (numericCount > values.length * 0.5) {
      return { name: header, auto: false, selected: false, sample: values.slice(0, 2).map(String) };
    }
    const strVals = values.filter(v => typeof v === 'string');
    const avgLen = strVals.length > 0
      ? strVals.reduce((s, v) => s + v.length, 0) / strVals.length : 0;
    const translatable = avgLen > 10;
    return {
      name: header,
      auto: translatable,
      selected: translatable,
      sample: strVals.slice(0, 2).map(v => v.length > 50 ? v.slice(0, 50) + '…' : v),
    };
  });
}

function TranslateFilePage() {
  const fileRef = useRef(null);

  // ── File state ─────────────────────────────────────────────────────────────
  const [fileName, setFileName]         = useState('');
  const [fileType, setFileType]         = useState('');   // 'xlsx'|'csv'|'docx'|'txt'
  const [rawHeaders, setRawHeaders]     = useState([]);
  const [rawRows, setRawRows]           = useState([]);
  const [docContent, setDocContent]     = useState('');
  const [columns, setColumns]           = useState([]);   // { name, auto, selected, sample }
  const [fileError, setFileError]       = useState('');
  const [dragging, setDragging]         = useState(false);

  // ── Settings ──────────────────────────────────────────────────────────────
  const [sourceLang, setSourceLang]         = useState('SE');
  const [targetMarkets, setTargetMarkets]   = useState(['NO']);
  const [context, setContext]               = useState('');
  const [outputFormat, setOutputFormat]     = useState('docx'); // 'docx'|'txt'|'xlsx'

  // ── Progress / results ────────────────────────────────────────────────────
  const [running, setRunning]               = useState(false);
  const [progress, setProgress]             = useState({ done: 0, total: 0 });
  const [translatedData, setTranslatedData] = useState({}); // { mkt: row[]|string }

  // ── Vocab / prompt context ────────────────────────────────────────────────
  const dataCtx = useData();
  const vocabCollections = dataCtx.vocabCollections || ['Standard'];
  const [activeCollections, setActiveCollections] = useState(null);
  const vocabForPrompt = activeCollections
    ? (dataCtx.vocabulary || []).filter(e => activeCollections.includes(e.collection || 'Standard'))
    : (dataCtx.vocabulary || []);
  const promptCtx = {
    brandGuidelines: dataCtx.templates?.brandGuidelines || '',
    vocabulary: vocabForPrompt,
    model: dataCtx.model || 'gemini',
  };
  const addUsageLog = dataCtx.addUsageLog;
  const logTranslateUsage = (r, mkt) => {
    if (!addUsageLog || !r?.usage) return;
    addUsageLog({
      model:        r.usage.model,
      inputTokens:  r.usage.inputTokens || 0,
      outputTokens: r.usage.outputTokens || 0,
      market:       mkt,
      category:     null,
      source:       'translate-file',
      module:       'translate',
    });
  };

  // ── File parsing ──────────────────────────────────────────────────────────
  const resetFile = () => {
    setFileName(''); setFileType(''); setRawHeaders([]); setRawRows([]);
    setDocContent(''); setColumns([]); setFileError(''); setTranslatedData({});
    setProgress({ done: 0, total: 0 });
  };

  const processFile = async (file) => {
    if (!file) return;
    resetFile();
    const ext = file.name.split('.').pop().toLowerCase();
    setFileName(file.name);
    try {
      if (ext === 'xlsx' || ext === 'xls' || ext === 'csv') {
        // CSV: read as text (preserves UTF-8 å/ä/ö). Excel: read as binary array.
        let wb;
        if (ext === 'csv') {
          const text = await file.text();
          wb = XLSX.read(text, { type: 'string' });
        } else {
          const buf = await file.arrayBuffer();
          wb = XLSX.read(buf, { type: 'array' });
        }
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { defval: '' });
        if (!raw.length) { setFileError('Filen är tom.'); return; }
        const headers = Object.keys(raw[0]);
        setFileType(ext === 'csv' ? 'csv' : 'xlsx');
        setRawHeaders(headers);
        setRawRows(raw);
        setColumns(detectColumns(headers, raw));
      } else if (ext === 'docx') {
        const buf = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer: buf });
        const text = result.value.trim();
        if (!text) { setFileError('Inga texter hittades i dokumentet.'); return; }
        setFileType('docx');
        setDocContent(text);
      } else if (ext === 'txt') {
        const text = await file.text();
        if (!text.trim()) { setFileError('Filen är tom.'); return; }
        setFileType('txt');
        setDocContent(text.trim());
      } else {
        setFileError('Filformat stöds ej. Använd .xlsx, .csv, .docx eller .txt.');
      }
    } catch (err) {
      setFileError('Kunde inte läsa filen: ' + err.message);
    }
  };

  const handleFileEvent = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processFile(file);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragging(false); };

  const toggleMarket  = (code) => setTargetMarkets(prev =>
    prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  const toggleColumn  = (name) => setColumns(prev =>
    prev.map(c => c.name === name ? { ...c, selected: !c.selected } : c));

  // ── Translation run ───────────────────────────────────────────────────────
  const handleRun = async () => {
    if (!targetMarkets.length) return;
    setRunning(true);
    setTranslatedData({});
    const out = {};

    if (fileType === 'xlsx' || fileType === 'csv') {
      const selectedCols = columns.filter(c => c.selected).map(c => c.name);
      if (!selectedCols.length || !rawRows.length) { setRunning(false); return; }
      const total = rawRows.length * targetMarkets.length;
      let done = 0;
      setProgress({ done: 0, total });

      await Promise.all(targetMarkets.map(async (mkt) => {
        const translatedRows = [];
        for (const row of rawRows) {
          const newRow = { ...row };
          await Promise.all(selectedCols.map(async (col) => {
            const val = String(row[col] || '').trim();
            if (val) {
              try {
                const r = await translateText(val, sourceLang, mkt, context, promptCtx);
                newRow[col] = r.text;
                logTranslateUsage(r, mkt);
              } catch (_) {}
            }
          }));
          translatedRows.push(newRow);
          done += 1;
          setProgress({ done, total });
        }
        out[mkt] = translatedRows;
      }));
    } else {
      const paragraphs = docContent.split('\n').filter(p => p.trim());
      const total = paragraphs.length * targetMarkets.length;
      let done = 0;
      setProgress({ done: 0, total });

      await Promise.all(targetMarkets.map(async (mkt) => {
        const translated = [];
        for (const para of paragraphs) {
          try {
            const r = await translateText(para, sourceLang, mkt, context, promptCtx);
            translated.push(r.text);
            logTranslateUsage(r, mkt);
          } catch (_) { translated.push(para); }
          done += 1;
          setProgress({ done, total });
        }
        out[mkt] = translated.join('\n\n');
      }));
    }

    setTranslatedData(out);
    dataCtx.addLog({ type: 'translate-file', fileName, markets: targetMarkets,
      products: fileType === 'xlsx' || fileType === 'csv' ? rawRows.length : 1, durationSeconds: 0 });
    setRunning(false);
  };

  // ── Download ──────────────────────────────────────────────────────────────
  const downloadMarket = async (mkt) => {
    const data = translatedData[mkt];
    if (!data) return;
    const baseName = fileName.replace(/\.[^.]+$/, '');

    if (fileType === 'xlsx' || fileType === 'csv') {
      // Excel/CSV: always same format as input
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(data, { header: rawHeaders });
      XLSX.utils.book_append_sheet(wb, ws, mkt);
      XLSX.writeFile(wb, `${baseName}-${mkt}.${fileType === 'csv' ? 'csv' : 'xlsx'}`,
        fileType === 'csv' ? { bookType: 'csv' } : {});
    } else {
      // docx/txt input: honour chosen outputFormat
      const paragraphs = typeof data === 'string'
        ? data.split('\n\n').filter(p => p.trim())
        : data;

      if (outputFormat === 'docx') {
        const API = process.env.REACT_APP_BACKEND_URL;
        try {
          const resp = await fetch(`${API}/api/export/docx`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ paragraphs, filename: `${baseName}-${mkt}` }),
          });
          if (!resp.ok) throw new Error('Backend error');
          const blob = await resp.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url; a.download = `${baseName}-${mkt}.docx`; a.click();
          URL.revokeObjectURL(url);
        } catch (err) {
          // Fallback to txt if backend fails
          const text = paragraphs.join('\n\n');
          const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url; a.download = `${baseName}-${mkt}.txt`; a.click();
          URL.revokeObjectURL(url);
        }
      } else if (outputFormat === 'xlsx') {
        const sourceParagraphs = docContent.split('\n').filter(p => p.trim());
        const rows = paragraphs.map((p, i) => ({
          'Källtext': sourceParagraphs[i] || '',
          [mkt]: p,
        }));
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(rows);
        XLSX.utils.book_append_sheet(wb, ws, mkt);
        XLSX.writeFile(wb, `${baseName}-${mkt}.xlsx`);
      } else {
        // txt
        const text = typeof data === 'string' ? data : data.join('\n\n');
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `${baseName}-${mkt}.txt`; a.click();
        URL.revokeObjectURL(url);
      }
    }
  };

  const doneMarkets = Object.keys(translatedData);
  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
  const hasFile = !!fileType;
  const stepNum = (fileType === 'xlsx' || fileType === 'csv') ? 3 : 2;
  const canRun  = hasFile && targetMarkets.length > 0 && !running &&
    (fileType === 'docx' || fileType === 'txt'
      ? !!docContent
      : rawRows.length > 0 && columns.some(c => c.selected));

  return (
    <div className="tfile-wrap">

      {/* ── Step 1: Upload ─────────────────────────────────────────────────── */}
      <div className="tfile-section">
        <h3 className="tfile-section-title">1. Ladda upp fil</h3>
        <p className="tfile-hint">Stöder <strong>.xlsx</strong>, <strong>.csv</strong>, <strong>.docx</strong> och <strong>.txt</strong></p>

        <div
          className={`tfile-dropzone${hasFile ? ' tfile-dropzone--loaded' : ''}${dragging ? ' tfile-dropzone--drag' : ''}`}
          onClick={() => fileRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          data-testid="tfile-dropzone"
        >
          {hasFile ? (
            <>
              <FileSpreadsheet size={20} className="tfile-drop-icon" />
              <span className="tfile-drop-name">{fileName}</span>
              <span className="tfile-drop-sub">
                {fileType === 'xlsx' || fileType === 'csv'
                  ? `${rawRows.length} rader · ${columns.length} kolumner`
                  : `${docContent.split('\n').filter(p => p.trim()).length} stycken`}
              </span>
              <button className="tfile-drop-change"
                onClick={e => { e.stopPropagation(); resetFile(); }}
                data-testid="tfile-change-btn">
                Byt fil
              </button>
            </>
          ) : (
            <>
              <Upload size={22} className="tfile-drop-icon" />
              <span className="tfile-drop-label">Klicka eller dra hit en fil</span>
              <span className="tfile-drop-sub">.xlsx · .csv · .docx · .txt</span>
            </>
          )}
        </div>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv,.docx,.txt"
          style={{ display: 'none' }} onChange={handleFileEvent} data-testid="tfile-input" />
        {fileError && (
          <div className="tfile-error" data-testid="tfile-error">
            <AlertTriangle size={14} /> {fileError}
          </div>
        )}
      </div>

      {/* ── Step 2: Column selector (Excel / CSV only) ─────────────────────── */}
      {hasFile && (fileType === 'xlsx' || fileType === 'csv') && columns.length > 0 && (
        <div className="tfile-section" data-testid="tfile-col-selector">
          <h3 className="tfile-section-title">2. Välj kolumner att översätta</h3>
          <p className="tfile-hint">Kolumner med längre text är förmarkerade. Avmarkera ID- eller kodkolumner.</p>
          <div className="tfile-col-table">
            <div className="tfile-col-header">
              <span>Kolumn</span><span>Exempelvärden</span><span>Översätt?</span>
            </div>
            {columns.map(col => (
              <div key={col.name}
                className={`tfile-col-row${col.selected ? ' tfile-col-row--on' : ''}`}
                data-testid={`col-row-${col.name}`}>
                <span className="tfile-col-name">
                  {col.name}
                  <span className={`tfile-col-badge${col.auto ? ' tfile-col-badge--text' : ' tfile-col-badge--id'}`}>
                    {col.auto ? 'Text' : 'ID/Kod'}
                  </span>
                </span>
                <span className="tfile-col-sample">
                  {col.sample.map((s, i) => <span key={i} className="tfile-sample-chip">{s}</span>)}
                </span>
                <label className="tfile-col-check">
                  <input type="checkbox" checked={col.selected}
                    onChange={() => toggleColumn(col.name)}
                    data-testid={`col-check-${col.name}`} />
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Step 2/3: Settings ─────────────────────────────────────────────── */}
      {hasFile && (
        <div className="tfile-section" data-testid="tfile-settings">
          <h3 className="tfile-section-title">{stepNum}. Inställningar</h3>

          <div className="tfile-setting-group">
            <label className="tfile-setting-label">Källspråk</label>
            <div className="translate-market-chips">
              {SOURCE_LANGS.map(lang => (
                <button key={lang.code}
                  className={`translate-market-chip${sourceLang === lang.code ? ' translate-market-chip--on' : ''}`}
                  onClick={() => setSourceLang(lang.code)}
                  data-testid={`src-lang-${lang.code}`}>
                  <span>{lang.flag}</span><span>{lang.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="tfile-setting-group">
            <label className="tfile-setting-label">Målmarknader</label>
            <div className="translate-market-chips">
              {MARKETS.map(m => (
                <button key={m.code}
                  className={`translate-market-chip${targetMarkets.includes(m.code) ? ' translate-market-chip--on' : ''}`}
                  onClick={() => toggleMarket(m.code)} data-testid={`tfile-market-${m.code}`}>
                  <span>{m.flag}</span><span>{m.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="tfile-setting-group">
            <label className="tfile-setting-label">Instruktioner <span className="translate-optional">(valfritt)</span></label>
            <input className="translate-context-input" type="text"
              placeholder='T.ex. "Behåll teknisk terminologi" eller "Anpassa till B2C-ton"'
              value={context} onChange={e => setContext(e.target.value)}
              data-testid="tfile-context-input" />
          </div>

          <VocabCollectionSelector vocabCollections={vocabCollections}
            activeCollections={activeCollections} onChange={setActiveCollections} />

          {/* Output format selector — only for docx/txt input */}
          {(fileType === 'docx' || fileType === 'txt') && (
            <div className="tfile-setting-group" data-testid="tfile-format-selector">
              <label className="tfile-setting-label">Utdataformat</label>
              <div className="tfile-format-row">
                {[
                  { value: 'docx', label: 'Word (.docx)' },
                  { value: 'txt',  label: 'Ren text (.txt)' },
                  { value: 'xlsx', label: 'Excel (.xlsx)' },
                ].map(opt => (
                  <label key={opt.value}
                    className={`tfile-format-option${outputFormat === opt.value ? ' tfile-format-option--on' : ''}`}
                    data-testid={`tfile-format-${opt.value}`}>
                    <input type="radio" name="outputFormat" value={opt.value}
                      checked={outputFormat === opt.value}
                      onChange={() => setOutputFormat(opt.value)} />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          <button className="translate-btn" style={{ marginTop: 16, maxWidth: 320 }}
            onClick={handleRun} disabled={!canRun} data-testid="tfile-run-btn">
            {running
              ? <><span className="tfile-spinner" /> Översätter…</>
              : <><Download size={15} /> Starta översättning</>}
          </button>
        </div>
      )}

      {/* ── Progress ───────────────────────────────────────────────────────── */}
      {running && progress.total > 0 && (
        <div className="tfile-progress-wrap" data-testid="tfile-progress">
          <div className="tfile-progress-bar">
            <div className="tfile-progress-fill" style={{ width: `${pct}%` }} />
          </div>
          <span className="tfile-progress-label">{pct}% — {progress.done} / {progress.total}</span>
        </div>
      )}

      {/* ── Results ────────────────────────────────────────────────────────── */}
      {doneMarkets.length > 0 && !running && (
        <div className="tfile-section tfile-results" data-testid="tfile-results">
          <h3 className="tfile-section-title">Resultat</h3>
          <div className="tfile-result-grid">
            {doneMarkets.map(mkt => {
              const market = MARKETS.find(m => m.code === mkt);
              const count = fileType === 'xlsx' || fileType === 'csv'
                ? `${(translatedData[mkt] || []).length} rader`
                : `${(translatedData[mkt] || '').split('\n\n').length} stycken`;
              return (
                <div key={mkt} className="tfile-result-card" data-testid={`tfile-result-${mkt}`}>
                  <span className="tfile-result-flag">{market?.flag || mkt}</span>
                  <div className="tfile-result-info">
                    <strong>{market?.label || mkt}</strong>
                    <span className="tfile-result-sub">{count}</span>
                  </div>
                  <button className="vocab-btn vocab-btn--primary"
                    onClick={() => downloadMarket(mkt)} data-testid={`tfile-download-${mkt}`}>
                    <Download size={13} /> Ladda ner
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────
// MAIN SHELL with tabs
// ─────────────────────────────────────────────────────────────────────────────
export default function TranslatePage() {
  const [tab, setTab] = useState('text');
  return (
    <div className="page-scroll">
      <div className="page-header">
        <h1 className="page-title">Översätt</h1>
        <p className="page-subtitle">Brand-medveten översättning med rätt terminologi för varje marknad.</p>
      </div>
      <div className="translate-tabs" data-testid="translate-tabs">
        <button className={`translate-tab${tab === 'text' ? ' translate-tab--active' : ''}`}
          onClick={() => setTab('text')} data-testid="tab-text">
          <Languages size={14} /> Översätt text
        </button>
        <button className={`translate-tab${tab === 'file' ? ' translate-tab--active' : ''}`}
          onClick={() => setTab('file')} data-testid="tab-file">
          <FileSpreadsheet size={14} /> Översätt fil
        </button>
      </div>
      {tab === 'text' ? <TranslateTextContent /> : <TranslateFilePage />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TRANSLATE TEXT (extracted from original default export)
// ─────────────────────────────────────────────────────────────────────────────
function TranslateTextContent() {
  const [sourceText, setSourceText] = useState('');
  const [sourceLang, setSourceLang] = useState('SE');
  const [targetMarkets, setTargetMarkets] = useState([]); // UX 2: default empty
  const [context, setContext] = useState('');
  const [results, setResults] = useState({});
  const [loadingMarkets, setLoadingMarkets] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const { translationHistory: history, addToTranslationHistory, clearTranslationHistory, addLog, templates, vocabulary, vocabCollections, model, addUsageLog } = useData();
  const [activeCollections, setActiveCollections] = useState(null);
  const vocabForPrompt = activeCollections
    ? vocabulary.filter(e => activeCollections.includes(e.collection || 'Standard'))
    : vocabulary;
  const promptCtx = { brandGuidelines: templates.brandGuidelines, vocabulary: vocabForPrompt, model };

  // UX 2: Auto-deselect source market when sourceLang changes (if text is present)
  useEffect(() => {
    if (!sourceText.trim()) return;
    setTargetMarkets(prev =>
      prev.includes(sourceLang) ? prev.filter(c => c !== sourceLang) : prev
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceLang]);

  // UX 2: Auto-deselect when text is first entered (goes from empty to non-empty)
  const prevEmptyRef = useRef(true);
  useEffect(() => {
    const nowEmpty = !sourceText.trim();
    if (prevEmptyRef.current && !nowEmpty) {
      setTargetMarkets(prev =>
        prev.includes(sourceLang) ? prev.filter(c => c !== sourceLang) : prev
      );
    }
    prevEmptyRef.current = nowEmpty;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceText]);

  const toggleMarket = (code) => {
    setTargetMarkets(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  };

  const toggleAllMarkets = () => {
    setTargetMarkets(prev =>
      prev.length === MARKETS.length ? [] : MARKETS.map(m => m.code)
    );
  };

  const handleRetranslate = useCallback(async (marketCode) => {
    if (!sourceText.trim()) return;
    const market = MARKETS.find(m => m.code === marketCode);
    if (!market) return;

    setLoadingMarkets(prev => [...prev, marketCode]);
    try {
      const translateResult = await translateText(sourceText, sourceLang, marketCode, context, promptCtx);
      const text = translateResult.text;
      setResults(prev => ({ ...prev, [marketCode]: { text } }));
      if (translateResult.usage && addUsageLog) {
        addUsageLog({
          model:        translateResult.usage.model,
          inputTokens:  translateResult.usage.inputTokens || 0,
          outputTokens: translateResult.usage.outputTokens || 0,
          market:       marketCode,
          category:     null,
          source:       'translate-text',
          module:       'translate',
        });
      }
    } catch (err) {
      setResults(prev => ({ ...prev, [marketCode]: { error: err.message } }));
    } finally {
      setLoadingMarkets(prev => prev.filter(c => c !== marketCode));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceText, sourceLang, context, vocabForPrompt]);

  const handleTranslate = useCallback(async () => {
    if (!sourceText.trim() || targetMarkets.length === 0) return;

    const marketsToTranslate = MARKETS.filter(m => targetMarkets.includes(m.code));
    setResults({});
    setLoadingMarkets(marketsToTranslate.map(m => m.code));

    // Run all market translations in parallel
    const startTime = Date.now();
    const finalResults = {};
    await Promise.all(
      marketsToTranslate.map(async (market) => {
        try {
          const translateResult = await translateText(sourceText, sourceLang, market.code, context, promptCtx);
          const text = translateResult.text;
          finalResults[market.code] = { text };
          setResults(prev => ({ ...prev, [market.code]: { text } }));
          if (translateResult.usage && addUsageLog) {
            addUsageLog({
              model:        translateResult.usage.model,
              inputTokens:  translateResult.usage.inputTokens || 0,
              outputTokens: translateResult.usage.outputTokens || 0,
              market:       market.code,
              category:     null,
              source:       'translate-text',
              module:       'translate',
            });
          }
        } catch (err) {
          finalResults[market.code] = { error: err.message };
          setResults(prev => ({ ...prev, [market.code]: { error: err.message } }));
        } finally {
          setLoadingMarkets(prev => prev.filter(c => c !== market.code));
        }
      })
    );

    // Save metadata + full content to history and log
    addToTranslationHistory({
      snippet: sourceText.slice(0, 60), markets: targetMarkets, sourceLang,
      fullText: sourceText,
      results: Object.fromEntries(
        Object.entries(finalResults).map(([k, v]) => [k, v?.text || ''])
      ),
    });
    const textLog = { type: 'translate-text', productName: sourceText.slice(0, 60), markets: targetMarkets, products: 1, success: 1, failed: 0, durationSeconds: Math.round((Date.now() - startTime) / 1000) };
    addLog(textLog);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceText, sourceLang, targetMarkets, context, vocabForPrompt, addToTranslationHistory, addLog]);

  const handleClearHistory = () => {
    clearTranslationHistory();
  };

  const isTranslating = loadingMarkets.length > 0;
  const hasResults = Object.keys(results).length > 0;
  const allSelected = targetMarkets.length === MARKETS.length;

  return (
    <div className="translate-layout">
        {/* ── LEFT: Input panel ── */}
        <div className="translate-input-panel">

          {/* Source text */}
          <div className="translate-field-group">
            <div className="translate-field-header">
              <label className="translate-label">Källtext</label>
              <span className="translate-char-count translate-char-count--source">
                {sourceText.length} tecken
              </span>
            </div>
            <textarea
              className="translate-source-textarea"
              placeholder="Klistra in produktbeskrivning, kategoritext, e-post eller annat innehåll här…"
              value={sourceText}
              onChange={e => setSourceText(e.target.value)}
              data-testid="source-text-input"
            />
          </div>

          {/* Source language */}
          <div className="translate-field-group">
            <label className="translate-label">Källspråk</label>
            <div className="translate-select-wrap">
              <select
                className="translate-select"
                value={sourceLang}
                onChange={e => setSourceLang(e.target.value)}
                data-testid="source-lang-select"
              >
                {SOURCE_LANGS.map(l => (
                  <option key={l.code} value={l.code}>{l.label}</option>
                ))}
              </select>
              <ChevronDown size={14} className="translate-select-icon" />
            </div>
          </div>

          {/* Target markets */}
          <div className="translate-field-group">
            <div className="translate-field-header">
              <label className="translate-label">Målmarknader</label>
              <button
                className="translate-select-all-btn"
                onClick={toggleAllMarkets}
                data-testid="toggle-all-markets-btn"
              >
                {allSelected ? 'Avmarkera alla' : 'Välj alla'}
              </button>
            </div>
            <div className="translate-market-chips">
              {MARKETS.map(market => (
                <button
                  key={market.code}
                  className={`translate-market-chip${targetMarkets.includes(market.code) ? ' translate-market-chip--on' : ''}`}
                  onClick={() => toggleMarket(market.code)}
                  data-testid={`market-chip-${market.code}`}
                >
                  <span>{market.flag}</span>
                  <span>{market.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Context / instructions */}
          <div className="translate-field-group">
            <label className="translate-label">
              Ytterligare instruktioner <span className="translate-optional">(valfritt)</span>
            </label>
            <input
              className="translate-context-input"
              type="text"
              placeholder='T.ex. "Kategoriintrotext, behåll formell ton" eller "Förkorta till max 150 tecken"'
              value={context}
              onChange={e => setContext(e.target.value)}
              data-testid="context-input"
            />
          </div>

          {/* Vocabulary collection selector */}
          <VocabCollectionSelector
            vocabCollections={vocabCollections}
            activeCollections={activeCollections}
            onChange={setActiveCollections}
          />

          {/* Translate button */}
          <button
            className="translate-btn"
            onClick={handleTranslate}
            disabled={!sourceText.trim() || targetMarkets.length === 0 || isTranslating}
            data-testid="translate-btn"
          >
            <Languages size={16} />
            {isTranslating
              ? `Översätter… (${loadingMarkets.length} kvar)`
              : `Översätt till ${targetMarkets.length} marknad${targetMarkets.length !== 1 ? 'er' : ''}`}
          </button>

          {/* History toggle */}
          <button
            className="translate-history-toggle"
            onClick={() => setShowHistory(v => !v)}
            data-testid="history-toggle-btn"
          >
            <History size={13} />
            Tidigare översättningar ({history.length})
          </button>

          {showHistory && (
            <div className="translate-history-panel" data-testid="history-panel">
              <div className="translate-history-header">
                <span>Historik</span>
                {history.length > 0 && (
                  <button className="translate-history-clear" onClick={handleClearHistory}>
                    <RotateCcw size={12} /> Rensa
                  </button>
                )}
              </div>
              {history.length === 0 ? (
                <p className="translate-history-empty">Inga tidigare översättningar.</p>
              ) : (
                <ul className="translate-history-list">
                  {history.map(h => (
                    <li key={h.id}
                      className="translate-history-item translate-history-item--clickable"
                      onClick={() => {
                        setSourceText(h.fullText || h.snippet || '');
                        setSourceLang(h.sourceLang || 'SE');
                        setTargetMarkets(h.markets || []);
                        if (h.results) {
                          setResults(
                            Object.fromEntries(
                              Object.entries(h.results).map(([k, v]) => [k, { text: v }])
                            )
                          );
                        }
                        setShowHistory(false);
                      }}
                      title="Klicka för att återställa denna session"
                    >
                      <span className="translate-history-snippet">"{h.snippet}{h.snippet?.length >= 60 ? '…' : ''}"</span>
                      <span className="translate-history-markets">{h.markets.join(' · ')}</span>
                      <span className="translate-history-date">
                        {new Date(h.timestamp).toLocaleDateString('sv-SE', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* ── RIGHT: Output cards ── */}
        <div className="translate-output-panel">
          {(isTranslating || hasResults) ? (
            MARKETS.filter(m => targetMarkets.includes(m.code)).map(market => (
              <TranslationCard
                key={market.code}
                market={market}
                result={results[market.code]}
                isLoading={loadingMarkets.includes(market.code)}
                onRetranslate={handleRetranslate}
              />
            ))
          ) : (
            <div className="translate-output-empty">
              <Languages size={36} />
              <p>Fyll i texten och välj marknader<br />— översättningarna visas här.</p>
            </div>
          )}
        </div>
      </div>
  );
}
